import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {useMapSelector} from '../../../../hooks/map-selector';
import {DoorModel} from '../../../../types/door';
import {SelectItem} from '../../../../components/Select';
import {DoorBrandModel} from '../../../../types/door-brand';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {isValidDoorNumber} from '../../../../utils/validation';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/settings/door';

export const DoorEditScreen = memo(function DoorEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const doorList = useSelector(state => state.control.doorList);
    const doorBranchOptionList = useMapSelector(
        state => state.control.doorBrandList,
        doorBrand => ({value: doorBrand, label: doorBrand.name}),
    ) || [];

    const [selected, setSelected] = useState<DoorModel>(null);
    const [name, setName] = useState('');
    const [doorBrandOption, setDoorBrandOption] = useState<SelectItem<DoorBrandModel>>(null);
    const [doorPath, setDoorPath] = useState('');
    const [doorNumber, setDoorNumber] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_door_name'));

            return false;
        }

        if (doorBrandOption == null) {
            failure(t('pls_select_a_valid_door_brand'));

            return false;
        }

        if (doorPath == null || doorPath.length === 0) {
            failure(t('pls_enter_a_valid_url'));

            return false;
        }

        if (!isValidDoorNumber(doorNumber)) {
            failure(t('pls_enter_a_valid_door_number'));

            return false;
        }

        if (username == null || username.length === 0) {
            failure(t('pls_enter_a_valid_username'));

            return false;
        }

        if (password == null || password.length === 0) {
            failure(t('pls_enter_a_valid_password'));

            return false;
        }

        return true;
    }, [t, doorBrandOption, doorPath, doorNumber, failure, name, password, username]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        let doorPathWithScheme = doorPath;

        if (doorPathWithScheme.indexOf('http://') !== 0) {
            doorPathWithScheme = 'http://' + doorPath;
        }

        const doorModel: DoorModel = {
            id: selected?.id || null,
            name,
            door_brand_id: doorBrandOption.value.id,
            door_path: doorPathWithScheme,
            door_number: Number(doorNumber),
            username,
            password,
        };

        return rpc.updateDoor(doorModel)
            .then(() => {
                success(t(doorModel.id ? 'door_successfully_edited' : 'door_successfully_created'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        doorBrandOption?.value?.id,
        doorPath,
        doorNumber,
        failure,
        isFieldsValid,
        name,
        onBackPressed,
        password,
        rpc,
        selected?.id,
        success,
        username,
    ]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteDoor(selected.id)
            .then(() => {
                success(t('door_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, failure, onBackPressed, rpc, selected, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type='door'
                    questionText={t('are_you_sure_you_want_to_delete')}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue',{name:selected?.name, message})}
                    onPress={onDeletePressed}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        }], [t, selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const door = doorList.find(doorItem => doorItem.id === params['id']);
        if (door == null) {
            return;
        }

        setSelected(door);
        setName(door.name);
        setDoorBrandOption(
            doorBranchOptionList.find(doorBranchOption => doorBranchOption.value.id === door.door_brand_id) ||
            doorBranchOptionList[0] ||
            null);
        setDoorPath(door.door_path);
        setDoorNumber(door.door_number.toString());
        setUsername(door.username);
        setPassword(door.password);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setDoorBrandOption(doorBranchOptionList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_door_name')}
                            label={t('door_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('brand_model')}
                            options={doorBranchOptionList}
                            onChange={setDoorBrandOption}
                            value={doorBrandOption}/>
                        <SettingInputComponent
                            placeholder={t('write_relay_number')}
                            label={t('relay_number')}
                            value={doorNumber}
                            setValue={setDoorNumber}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={200}
                            placeholder={t('write_url')}
                            label={t('url_ip')}
                            value={doorPath}
                            setValue={setDoorPath}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_username')}
                            label={t('username')}
                            value={username}
                            setValue={setUsername}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            type='password'
                            placeholder={t('write_password')}
                            label={t('password')}
                            value={password}
                            setValue={setPassword}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
